import { defineStore } from 'pinia';
import router from '@/router/router';
import { buildOccurrenceTour } from './occurrenceTour';
import { DEFAULT_BLOCKED_MESSAGE, isStepSatisfied } from './tourExpectations';
import type { OccurrenceKind, TourDefinition, TourStep } from './tourTypes';

/**
 * Estado do uso guiado.
 *
 * Vive numa store, e não no componente, por um motivo concreto: o tour atravessa
 * rotas. Ele começa em qualquer tela, navega para Contratos, entra no detalhe do
 * contrato e segue dentro do assistente de ocorrência. Um estado preso ao componente
 * morreria na primeira navegação.
 *
 * Também sobrevive a recarregamento de página, gravando o progresso no
 * `sessionStorage` — quem está aprendendo dá F5 sem pensar, e perder o tour aí é
 * exatamente o momento em que a pessoa desiste e liga para o suporte.
 */

const STORAGE_KEY = 'almada:guided-tour';

/** Quanto esperar a tela refletir a ação (a busca passa pela API). */
const EXPECTATION_TIMEOUT_MS = 6000;
const EXPECTATION_POLL_MS = 200;

/**
 * De quanto em quanto tempo o tour olha a tela para se mover sozinho.
 *
 * O tour acompanha a pessoa em vez de esperar que ela avise: quando ela clica no
 * elemento certo, o passo seguinte entra por conta própria. Antes era preciso clicar
 * em "Já fiz isso", o que obrigava quem estava aprendendo a confirmar o óbvio — e a
 * ficar apertando botão quando a tela demorava um instante a mais para montar.
 */
const WATCH_MS = 350;

let watchTimer: number | undefined;

interface PersistedTour {
	kind: OccurrenceKind;
	stepIndex: number;
	/**
	 * Precisa ser persistido, e não reconstruído: é ele que decide se o roteiro termina
	 * no passo que CRIA a ocorrência de verdade. Sem ele no storage, um F5 remontava o
	 * tour pelo ramo não-simulado — com o portão fechado e sem a faixa "MODO TUTORIAL"
	 * na tela — e levava a pessoa até o ponto sem volta achando que ainda treinava.
	 */
	simulated: boolean;
}

/**
 * Havia um tutorial simulado em andamento antes do F5?
 *
 * Fica fora da store, e é síncrona, porque quem pergunta precisa da resposta ANTES de
 * abrir o portão: abrir primeiro e descobrir depois deixaria, em todo carregamento de
 * página, uma janela de um microtask com a rede interceptada para quem não está em
 * tutorial nenhum.
 */
export function hasSimulatedTourSaved(): boolean {
	return readPersisted()?.simulated === true;
}

function readPersisted(): PersistedTour | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);

		return raw ? (JSON.parse(raw) as PersistedTour) : null;
	} catch {
		return null;
	}
}

export const useGuidedTourStore = defineStore('guidedTour', {
	state: () => ({
		/** Painel do '?' aberto. */
		isPanelOpen: false,
		/** Em qual passo do menu do painel a pessoa está. */
		panelStage: 'menu' as 'menu' | 'occurrence-kind',
		tour: null as TourDefinition | null,
		stepIndex: 0,
		/** O tour atual roda sobre o portão do tutorial, sem tocar a API. */
		simulated: false,
		/** A pessoa passou do ponto em que a ação vira real. */
		acknowledgedRealAction: false,
		/** Recado de "ainda não te vejo lá", quando o passo não foi cumprido. */
		blockedMessage: null as string | null,
		/** O passo atual existe, mas a tela ainda não está pronta para mostrá-lo. */
		waiting: false,
	}),

	getters: {
		isRunning: (state) => state.tour !== null,
		currentStep: (state) => (state.tour ? state.tour.steps[state.stepIndex] : null),
		totalSteps: (state) => state.tour?.steps.length ?? 0,
		isLastStep: (state) => !!state.tour && state.stepIndex === state.tour.steps.length - 1,
		progressPercent: (state) =>
			state.tour ? Math.round(((state.stepIndex + 1) / state.tour.steps.length) * 100) : 0,
	},

	actions: {
		togglePanel() {
			this.isPanelOpen = !this.isPanelOpen;

			if (!this.isPanelOpen) this.panelStage = 'menu';
		},

		closePanel() {
			this.isPanelOpen = false;
			this.panelStage = 'menu';
		},

		chooseOccurrence() {
			this.panelStage = 'occurrence-kind';
		},

		backToMenu() {
			this.panelStage = 'menu';
		},

		async startOccurrenceTour(kind: OccurrenceKind, options?: { simulated?: boolean }) {
			this.simulated = !!options?.simulated;
			this.tour = buildOccurrenceTour(kind, { simulated: this.simulated });
			this.stepIndex = 0;
			this.acknowledgedRealAction = false;
			this.blockedMessage = null;
			this.closePanel();
			this.persist(kind);
			this.scrollToTop();
			await this.applyStepRoute();
			this.startWatching();
		},

		/**
		 * Acompanha a tela e move o tour sozinho.
		 *
		 * Faz duas coisas a cada olhada: segura o passo enquanto a tela não estiver
		 * pronta para ele (`ready`), e avança quando a pessoa cumpre um passo de ação.
		 * As duas existem pelo mesmo motivo — quem está aprendendo não deveria precisar
		 * confirmar ao guia o que o guia consegue ver.
		 */
		startWatching() {
			this.stopWatching();

			watchTimer = window.setInterval(() => {
				const step = this.currentStep;

				if (!step) return;

				const route = router.currentRoute.value;

				this.waiting = !!step.ready && !isStepSatisfied({ ...step, expect: step.ready }, route);

				if (this.waiting) return;

				if (step.advance === 'action' && isStepSatisfied(step, route)) {
					void this.next();
				}
			}, WATCH_MS);
		},

		stopWatching() {
			if (watchTimer) window.clearInterval(watchTimer);
			watchTimer = undefined;
		},

		/**
		 * Retoma depois de um F5, se havia tour em andamento. Devolve se retomou.
		 *
		 * Só retoma o que foi gravado como simulado. O ramo não-simulado não é retomável
		 * daqui por construção: ele termina numa ação real, e reabri-lo exigiria garantias
		 * que esta store não tem como dar sozinha. Quem reabre o portão e acende a faixa é
		 * o `tutorialStore`, que chama este método — sozinho, este restore não basta.
		 */
		async restore(): Promise<boolean> {
			const saved = readPersisted();

			if (!saved?.simulated) {
				// Inclui o caso de um storage antigo, gravado antes de `simulated` existir:
				// não dá para saber o que era, então não se adivinha.
				if (saved) this.stop();

				return false;
			}

			this.simulated = true;
			this.tour = buildOccurrenceTour(saved.kind, { simulated: true });
			this.stepIndex = Math.min(Math.max(saved.stepIndex, 0), this.tour.steps.length - 1);
			this.blockedMessage = null;
			this.waiting = false;
			this.startWatching();

			return true;
		},

		persist(kind?: OccurrenceKind) {
			if (!this.tour) return;

			const resolved = kind ?? (this.tour.id.split('-')[1]?.toUpperCase() as OccurrenceKind);

			try {
				sessionStorage.setItem(
					STORAGE_KEY,
					JSON.stringify({
						kind: resolved,
						stepIndex: this.stepIndex,
						simulated: this.simulated,
					} satisfies PersistedTour),
				);
			} catch {
				// sessionStorage indisponível (aba anônima com storage bloqueado): o tour
				// continua funcionando, só não sobrevive a um F5.
			}
		},

		async next() {
			if (!this.tour) return;

			this.blockedMessage = null;
			this.waiting = false;

			if (this.isLastStep) {
				this.stop();

				return;
			}

			this.stepIndex += 1;
			this.persist();

			// Passo que troca de tela começa do topo dela, como aconteceria num clique
			// normal do menu.
			if (this.currentStep?.route) this.scrollToTop();

			await this.applyStepRoute();
		},

		async previous() {
			if (!this.tour || this.stepIndex === 0) return;

			this.blockedMessage = null;
			this.stepIndex -= 1;
			this.persist();
			await this.applyStepRoute();
		},

		/**
		 * O "Já fiz isso" do balão.
		 *
		 * Confere antes de avançar. Se a pessoa continua na tela anterior, avançar
		 * seria o pior dos mundos: o próximo passo apontaria para um elemento que não
		 * existe e ela ficaria olhando um balão solto no meio da tela, sem entender o
		 * que deu errado. Melhor segurar e dizer, sem culpa, o que ainda falta.
		 */
		async confirmAction() {
			const step = this.currentStep;

			if (!step) return;

			if (step.advance !== 'action' || isStepSatisfied(step, router.currentRoute.value)) {
				await this.next();

				return;
			}

			this.blockedMessage = step.expect?.message ?? DEFAULT_BLOCKED_MESSAGE;
		},

		/**
		 * Avança quando a pessoa fez a ação real no elemento destacado, em vez de
		 * clicar em "Próximo". Ignorado se o passo atual não for de ação, para um
		 * clique solto na tela não empurrar o tour.
		 */
		async completeAction(targetId: string) {
			const step = this.currentStep;

			if (!step || step.advance !== 'action') return;
			if ((step.actionTarget ?? step.target) !== targetId) return;

			// A ação foi feita, mas o efeito dela pode demorar: clicar em "Buscar"
			// dispara uma chamada à API, e avançar antes da lista voltar destacaria o
			// contrato errado. Então espera o resultado aparecer em vez de confiar no
			// clique.
			const satisfied = await this.waitForStep(step);

			if (satisfied) await this.next();
			else this.blockedMessage = step.expect?.message ?? DEFAULT_BLOCKED_MESSAGE;
		},

		/** Aguarda a tela refletir o passo, desistindo depois de alguns segundos. */
		async waitForStep(step: TourStep): Promise<boolean> {
			const deadline = Date.now() + EXPECTATION_TIMEOUT_MS;

			while (Date.now() < deadline) {
				if (isStepSatisfied(step, router.currentRoute.value)) return true;

				await new Promise((resolve) => setTimeout(resolve, EXPECTATION_POLL_MS));
			}

			return isStepSatisfied(step, router.currentRoute.value);
		},

		/**
		 * Sair de vez: encerra o guia e volta para a tela de boas-vindas.
		 *
		 * Diferente de `stop`, que só desmonta o guia e deixa a pessoa onde estava.
		 * Quem clica em "Sair" no meio do tutorial está perdida ou sem tempo, e largá-la
		 * na tela de contratos, no meio de um assistente aberto, é pior do que tê-la
		 * trazido até aqui. O caminho de volta é o lugar de onde ela veio.
		 */
		async leave() {
			this.stop();

			try {
				await router.push('/');
			} catch {
				// Navegação abortada por guard não deve impedir a saída do guia, que já
				// aconteceu na linha acima.
			}
		},

		stop() {
			this.stopWatching();
			this.tour = null;
			this.stepIndex = 0;
			this.simulated = false;
			this.acknowledgedRealAction = false;
			this.blockedMessage = null;
			this.waiting = false;

			try {
				sessionStorage.removeItem(STORAGE_KEY);
			} catch {
				// idem
			}
		},

		async applyStepRoute() {
			const step = this.currentStep;

			if (!step?.route) return;

			try {
				await router.push(step.route);
			} catch {
				// Navegação abortada (guard ou rota repetida) não deve derrubar o tour.
			}
		},

		/**
		 * Leva a página ao topo.
		 *
		 * O tutorial começa por um botão no fim do manual, então a página está rolada
		 * lá embaixo quando o guia aparece. Os primeiros passos não têm alvo na tela e
		 * não puxam a rolagem sozinhos, e o resultado é a pessoa vendo um balão falar
		 * de um destaque que está fora da tela.
		 */
		scrollToTop() {
			try {
				window.scrollTo({ top: 0, behavior: 'auto' });

				// O app rola dentro do NLayoutContent, não no window: sem isto o topo
				// real da área de conteúdo continua onde estava.
				document
					.querySelectorAll('.n-layout-scroll-container, .n-scrollbar-container')
					.forEach((el) => (el.scrollTop = 0));
			} catch {
				// Ambiente sem DOM completo (teste, SSR): não é motivo para não começar.
			}
		},
	},
});

export default useGuidedTourStore;
